/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, withRouter } from 'react-router-dom';
import { observer } from 'mobx-react';
import minimapModule from 'diagram-js-minimap';

import { shareService, trackingService } from 'services';
import { diagramControlStore, notificationStore, currentDiagramStore } from 'stores';
import { CamundaLogo } from 'icons';
import { Breadcrumb, TopBar, Modeler, PasswordProtectedShare, DiagramControls } from 'components';
import { Spinner } from 'primitives';
import { getPageTitle } from 'utils/helpers';
import { DMN, FILE_TYPE_MAPPING } from 'utils/constants';

import { documentationOverlayModule } from './show-documentation-overlay';

export const Share = withRouter(
  observer(() => {
    // @ts-expect-error TS2339
    const { id: shareId } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [diagram, setDiagram] = useState(null);

    useEffect(() => {
      trackingService.trackPageView('share');

      shareService
        .fetchById(shareId)
        .then((share) => {
          onDiagramLoaded(share.file);
        })
        .catch(({ status }) => {
          if (status === 401) {
            setIsLoading(false);
          } else {
            notificationStore.showError('Something went wrong, please try again later.');
          }
        });

      return () => {
        diagramControlStore.reset();
      };
    }, [shareId]);

    const onModelerLoaded = (modeler) => {
      diagramControlStore.setModeler(modeler);
    };

    const onDiagramLoaded = (diagram) => {
      setIsLoading(false);
      setDiagram(diagram);
      currentDiagramStore.trackDiagramView('share', {
        fileType: FILE_TYPE_MAPPING[diagram.type]
      });
    };

    const getAdditionalModules = () => {
      if (diagram.type === DMN) {
        return {
          drd: [minimapModule]
        };
      }

      return [minimapModule, documentationOverlayModule];
    };

    if (isLoading) {
      return <Spinner fullHeight />;
    }

    if (!diagram) {
      return <PasswordProtectedShare shareId={shareId} onSuccess={onDiagramLoaded} />;
    }

    return (
      <>
        <Helmet>
          <title>{getPageTitle(diagram.name)}</title>
        </Helmet>

        <TopBar.Breadcrumbs>
          <Breadcrumb title={diagram.name} variant="text" data-test="breadcrumb-diagram" />
        </TopBar.Breadcrumbs>

        <Modeler
          fitViewPort
          isViewOnly
          diagram={diagram}
          initialContent={diagram.content}
          additionalModules={getAdditionalModules()}
          bottomLeft={<CamundaLogo width="32" height="32" />}
          bottomRight={<DiagramControls />}
          onModelerLoaded={onModelerLoaded}
        />
      </>
    );
  })
);
